import React, { useState } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography
} from '@mui/material';
import {
  Dashboard,
  Insights,
  Theaters,
  TrendingUp,
  FilterCenterFocus
} from '@mui/icons-material';
import { brandColors } from '../../../../theme/theme';

// Import existing components
import AdvancedMetrics from './AdvancedMetrics';
import AwareInterestedQuadrant from './AwareInterestedQuadrant';
import TheaterPaymentQuadrant from './TheaterPaymentQuadrant';
import EthnicityTrendChart from './EthnicityTrendChart';
import AgeTrendChart from './AgeTrendChart';

// Import new components
import DemographicPerformanceDashboard from './DemographicPerformanceDashboard';
import OpeningWeekendAnalysis from './OpeningWeekendAnalysis';
import TheaterOpportunityMatrix from './TheaterOpportunityMatrix';
import MultiMetricTrends from './MultiMetricTrends';
import ConversionFunnelAnalysis from './ConversionFunnelAnalysis';

// Import fallback components (for when new API isn't available)
import AwarenessFunnel from './AwarenessFunnel';
import ConversionFlowChart from './ConversionFlowChart';

/**
 * DeepDiveTabs Component
 * 
 * Elegant tabbed interface for Deep Dive analytics organized into 5 sections:
 * 1. Overview - Advanced Metrics & Performance Dashboard
 * 2. Awareness Analysis - Quadrants & Opportunity Analysis
 * 3. Theater Intent - Theater metrics & Opening Weekend
 * 4. Trends Over Time - Multi-metric trends across all rounds
 * 5. Conversion Funnel - Flow analysis & funnel visualization
 */
const DeepDiveTabs = ({ data, trendsData, imdbId }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [useFallbackFunnel, setUseFallbackFunnel] = useState(false);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const tabs = [
    { label: 'Overview', icon: <Dashboard /> },
    { label: 'Awareness Analysis', icon: <Insights /> },
    { label: 'Theater Intent', icon: <Theaters /> },
    { label: 'Trends Over Time', icon: <TrendingUp /> },
    { label: 'Conversion Funnel', icon: <FilterCenterFocus /> }
  ];

  return (
    <Box>
      {/* Elegant Tab Navigation */}
      <Paper 
        elevation={0}
        sx={{ 
          mb: 3,
          borderRadius: 2,
          overflow: 'hidden',
          border: `1px solid ${brandColors.primary}20`
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              fontSize: '0.875rem',
              fontWeight: 600,
              textTransform: 'none',
              minHeight: 72,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: `${brandColors.primary}08`,
                transform: 'translateY(-2px)'
              }
            },
            '& .Mui-selected': {
              color: brandColors.primary,
              backgroundColor: `${brandColors.primary}10`
            },
            '& .MuiTabs-indicator': {
              height: 3,
              backgroundColor: brandColors.primary,
              borderRadius: '3px 3px 0 0'
            }
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box>
        {/* Tab 1: Overview */}
        {activeTab === 0 && (
          <Box>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700, 
                mb: 3,
                color: brandColors.primary 
              }}
            >
              Performance Overview
            </Typography>
            
            {/* Advanced Metrics Cards */}
            <AdvancedMetrics data={data.rawData} />

            {/* Demographic Performance Dashboard */}
            <DemographicPerformanceDashboard data={data.rawData} />
          </Box>
        )}

        {/* Tab 2: Awareness Analysis */}
        {activeTab === 1 && (
          <Box>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700, 
                mb: 3,
                color: brandColors.primary 
              }}
            >
              Awareness & Interest Analysis
            </Typography>

            {/* Aware vs Interested Quadrant */}
            <AwareInterestedQuadrant data={data.rawData} />
          </Box>
        )}

        {/* Tab 3: Theater Intent */}
        {activeTab === 2 && (
          <Box>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700, 
                mb: 3,
                color: brandColors.primary 
              }}
            >
              Theatrical Performance & Intent
            </Typography>

            {/* Theater vs Payment Quadrant */}
            <TheaterPaymentQuadrant data={data.rawData} />

            {/* Opening Weekend Analysis Dashboard */}
            <OpeningWeekendAnalysis data={data.rawData} />

            {/* Theater Opportunity Matrix */}
            <TheaterOpportunityMatrix data={data.rawData} />
          </Box>
        )}

        {/* Tab 4: Trends Over Time */}
        {activeTab === 3 && (
          <Box>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700, 
                mb: 3,
                color: brandColors.primary 
              }}
            >
              Performance Trends Across Survey Rounds
            </Typography>

            {/* Multi-Metric Trend Comparison */}
            <MultiMetricTrends data={data.rawData} trendsData={trendsData} />

            {/* Ethnicity Trends */}
            {trendsData && trendsData.ethnicityTrends && (
              <EthnicityTrendChart trendData={trendsData.ethnicityTrends} />
            )}

            {/* Age Trends */}
            {trendsData && trendsData.ageTrends && (
              <AgeTrendChart trendData={trendsData.ageTrends} />
            )}
          </Box>
        )}

        {/* Tab 5: Conversion Funnel */}
        {activeTab === 4 && (
          <Box>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700, 
                mb: 3,
                color: brandColors.primary 
              }}
            >
              Audience Conversion & Flow Analysis
            </Typography>

            {/* Try Enhanced Funnel, fallback to basic if API unavailable */}
            {!useFallbackFunnel ? (
              <ConversionFunnelAnalysis 
                imdbId={imdbId}
                onApiUnavailable={() => setUseFallbackFunnel(true)}
              />
            ) : (
              <Box>
                {/* Fallback: Original conversion components */}
                <AwarenessFunnel data={data.rawData} />
                <ConversionFlowChart data={data.rawData} />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DeepDiveTabs;

